import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DocumentPinData } from 'src/app/shared/models/document-pin.model';
import { skipAuth } from 'src/app/core/interceptors/auth-context';

@Injectable({
  providedIn: 'root'
})
export class TasksService {
  private envUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  transcribeAudio(audioBlob: Blob): Observable<string> {
    const extMap: Record<string, string> = {
      'audio/webm': 'webm', 'audio/mp4': 'mp4', 'audio/ogg': 'ogg',
      'audio/wav': 'wav', 'audio/mpeg': 'mp3', 'audio/mp3': 'mp3',
      'audio/m4a': 'm4a', 'audio/x-m4a': 'm4a', 'audio/aac': 'm4a'
    };
    const base = (audioBlob.type || '').split(';')[0].trim();
    const ext = extMap[base] || 'webm';
    const formData = new FormData();
    formData.append('audio', audioBlob, `recording.${ext}`);
    return this.http.post(`${this.envUrl}/tasks/transcribe`, formData).pipe(
      map((res: any) => res?.data?.text ?? ''),
      catchError(() => of(''))
    );
  }

  listEntities(): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/entities`)
    .pipe(
      map((res) => res),
      catchError((error: unknown) => throwError(() => error))
    );
  }

  getDocumentPins(projectId: string, documentId: string): Observable<DocumentPinData[]> {
    return this.http.get(`${this.envUrl}/tasks/document-pins/${projectId}/${encodeURIComponent(documentId)}`).pipe(
      map((res: any) => (res?.data ?? res) || []),
      catchError(() => of([]))
    );
  }

  filterInformation() {
    return this.http.get(`${this.envUrl}/projects/filter`)
    .pipe(
      map((res:any) => res.data),
      catchError((error: unknown) => throwError(() => error))
    );
  }

  listTasks(): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks`)
    .pipe(
      map((res: any) => res.data),
      catchError((error: unknown) => throwError(() => error))
    )
  }

  getTaskById(taskId: string | null): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/${taskId}`)
    .pipe(
      map((res: any) => res.data),
      catchError((error: unknown) => throwError(() => error))
    )
  }

  createTask(body: any, context?: HttpContext): Observable<any> {
    return this.http.post(`${this.envUrl}/tasks`, body, {
      context,
    })
    .pipe(
      map((res: any) => res),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  createSharedTask(sharedLinkId: string | null, body: any): Observable<any> {
    return this.http.post(`${this.envUrl}/tasks/shared/${sharedLinkId}`, body)
    .pipe(
      map((res: any) => res),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  updateTask(taskId: string | null, body: any): Observable<any> {
    return this.http.put(`${this.envUrl}/tasks/${taskId}`, body)
      .pipe(
        map((res: any) => res.data),
        catchError((error: any): Observable<any> => error)
      );
  }

  deleteTask(taskId: string | null, context?: HttpContext): Observable<any> {
    return this.http
      .delete(`${this.envUrl}/tasks/${taskId}`, {
        context,
      })
      .pipe(
        map((res) => res),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      )
  }

  deleteSharedTask(sharedLinkId: string | null, taskId: string | null): Observable<any> {
    return this.http
      .delete(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}`)
      .pipe(
        map((res) => res),
        catchError((error: any): Observable<any> => error)
      )
  }

  addImage(taskId: string | null, data: any, context?: HttpContext): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('files', data);

    return this.http.put(`${this.envUrl}/tasks/${taskId}/picture`, formData, {
      context,
    })
    .pipe(
      map((res) => res),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    )
  }

  listComments(taskId: string | null, ): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/${taskId}/comments`)
    .pipe(
      map((res: any) => res.data),
      catchError((error: unknown) => throwError(() => error))
    )
  }

  getCommentById(taskId: string | null, commentId: string | null): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/${taskId}/comments/${commentId}`)
    .pipe(
      map((res: any) => res.data),
      catchError((err: any): Observable<any> => err)
    )
  }

  addComment(taskId: string | null, body: any, imageBlob?: Blob): Observable<any> {
    if (imageBlob) {
      const formData = new FormData();
      formData.append('text', body?.text ?? '');
      formData.append('files', imageBlob, 'comment-photo.jpg');
      return this.http.post(`${this.envUrl}/tasks/${taskId}/comments`, formData).pipe(
        map((res) => res),
        catchError((error: any): Observable<any> => error)
      );
    }
    return this.http.post(`${this.envUrl}/tasks/${taskId}/comments`, body)
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

  updateComment(taskId: string | null, commentId: string | null, body: any): Observable<any> {
    return this.http.put(`${this.envUrl}/tasks/${taskId}/comments/${commentId}`, body)
    .pipe(
      map((res: any) => res.data),
      catchError((error: any): Observable<any> => error)
    );
  }

  deleteComment(taskId: string | null, commentId: string | null): Observable<any> {
    return this.http.delete(`${this.envUrl}/tasks/${taskId}/comments/${commentId}`)
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

  deleteImage(taskId: string | null, pictureKey: string | null): Observable<any> {
    return this.http.delete(`${this.envUrl}/tasks/${taskId}/picture/${pictureKey}`)
      .pipe(
        map((res) => res),
        catchError((error: any): Observable<any> => error)
      )
  }

  addImageToSharedTasks(sharedLinkId: string | null, taskId: string | null, data: any): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('files', data);

    return this.http.put(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/picture`, formData, {
      context: skipAuth(),
    })
    .pipe(
      map((res) => res),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    )
  }

  listSharedTasks(sharedLinkId: string | null): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/shared/${sharedLinkId}`, {
      context: skipAuth(),
    })
    .pipe(
      map((res: any) => res.data),
      catchError((err: any): Observable<any> => err)
    )
  }

  convertFromDefect(targetJournalId: string, taskIds: string[]): Observable<any> {
    return this.http.patch(`${this.envUrl}/tasks/convert-from-defect`, {
      targetJournalId,
      taskIds
    }).pipe(
      map((res: any) => res),
      catchError((error: any): Observable<any> => error)
    );
  }

  getSharedTaskById(sharedLinkId: string | null, taskId: string | null): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}`)
    .pipe(
      map((res: any) => res.data),
      catchError((err: any): Observable<any> => err)
    )
  }

  updateSharedTask(sharedLinkId: string | null, taskId: string | null, body: any): Observable<any> {
    return this.http.put(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}`, body)
      .pipe(
        map((res: any) => res.data),
        catchError((error: any): Observable<any> => error)
      );
  }

  deleteSharedTaskImage(sharedLinkId: string | null, taskId: string | null, pictureKey: any): Observable<any> {
    return this.http.delete(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/picture/${pictureKey}`, {
      context: skipAuth(),
    })
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

  // shared link comments
  addExternalSharedComment(sharedLinkId: string | null, taskId: string | null, body: any): Observable<any> {
    return this.http.post(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments/external`, body, {
      context: skipAuth(),
    })
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

  addSharedComment(sharedLinkId: string | null, taskId: string | null, body: any): Observable<any> {
    return this.http.post(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments`, body)
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

  getSharedCommentById(sharedLinkId: string | null, taskId: string | null, commentId: string | null): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments/${commentId}`, {
      context: skipAuth(),
    })
    .pipe(
      map((res: any) => res.data),
      catchError((err: any): Observable<any> => err)
    )
  }

  listSharedComment(sharedLinkId: string | null, taskId: string | null): Observable<any> {
    return this.http.get(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments`)
    .pipe(
      map((res: any) => res.data),
      catchError((err: any): Observable<any> => err)
    )
  }

  updateSharedComment(sharedLinkId: string | null, taskId: string | null, commentId: string | null, body: any): Observable<any> {
    return this.http.put(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments/${commentId}`, body)
    .pipe(
      map((res: any) => res.data),
      catchError((error: any): Observable<any> => error)
    );
  }

  updateSharedCommentExternal(sharedLinkId: string | null, taskId: string | null, commentId: string | null, body: any): Observable<any> {
    return this.http.put(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments/external/${commentId}`, body, {
      context: skipAuth(),
    })
    .pipe(
      map((res: any) => res.data),
      catchError((error: any): Observable<any> => error)
    );
  }

  deleteSharedComment(sharedLinkId: string | null, taskId: string | null, commentId: string | null): Observable<any> {
    return this.http.delete(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments/${commentId}`, {
      context: skipAuth(),
    })
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

  deleteSharedCommentExternal(sharedLinkId: string | null, taskId: string | null, commentId: string | null): Observable<any> {
    return this.http.delete(`${this.envUrl}/tasks/shared/${sharedLinkId}/${taskId}/comments/external/${commentId}`, {
      context: skipAuth(),
    })
    .pipe(
      map((res) => res),
      catchError((error: any): Observable<any> => error)
    )
  }

}
